import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { ConsentService } from './consent.service';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const GA_ID = 'G-CY56F8YVJC';

/**
 * Lädt Google Analytics 4 ausschließlich nach aktivem Opt-in (§ 25 TDDDG):
 * hört auf den ConsentService, injiziert `gtag.js` genau einmal, feuert
 * `consent update` + `config` und sendet danach SPA-Page-Views bei jeder
 * Navigation. Bei Widerruf wird das `analytics_storage`-Signal auf `denied`
 * gesetzt → GA4 stoppt Cookies/Hits.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly consent = inject(ConsentService);

  private scriptLoaded = false;
  private trackingActive = false;
  private pageViewsHooked = false;
  /** Zuletzt manuell getrackter Pfad – dedupliziert doppelte page_views. */
  private lastTrackedPath: string | null = null;

  /** Aus AppComponent-Constructor genau einmal aufrufen. */
  init(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.consent.state$.subscribe((state) => {
      if (state?.analytics) this.enable();
      else this.disable();
    });
  }

  private enable(): void {
    if (this.trackingActive) return;
    this.gtag('consent', 'update', {
      analytics_storage: 'granted',
      ad_storage: 'denied', // bis Marketing-Consent separat genutzt wird
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
    this.loadScript();
    // Page-Views komplett manuell steuern, damit der initiale View nicht doppelt
    // gezählt wird (config-Auto-View + erster NavigationEnd bei gespeichertem Consent).
    this.gtag('config', GA_ID, { anonymize_ip: true, send_page_view: false });
    this.trackingActive = true;
    this.hookPageViews();
    // Initialen View für die aktuelle URL einmalig senden.
    this.sendPageView(this.router.url);
  }

  private disable(): void {
    // Wenn GA bereits geladen war (Widerruf nach Zustimmung): denied-Signal
    // senden, damit GA4 keine weiteren Cookies/Hits erzeugt.
    if (this.scriptLoaded) {
      this.gtag('consent', 'update', { analytics_storage: 'denied' });
    }
    this.trackingActive = false;
  }

  private loadScript(): void {
    if (this.scriptLoaded) return;
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(s);
    this.scriptLoaded = true;
  }

  private hookPageViews(): void {
    if (this.pageViewsHooked) return;
    this.pageViewsHooked = true;
    // GA4 SPA: page_views werden manuell pro Navigation gesendet. Dedupe gegen
    // den zuletzt getrackten Pfad, damit der initiale View (aus enable()) nicht
    // vom ersten NavigationEnd doppelt gezählt wird.
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        if (!this.trackingActive) return;
        if (e.urlAfterRedirects === this.lastTrackedPath) return;
        this.sendPageView(e.urlAfterRedirects);
      });
  }

  private sendPageView(path: string): void {
    this.lastTrackedPath = path;
    this.gtag('event', 'page_view', {
      page_path: path,
      page_location: location.href,
      page_title: document.title,
    });
  }

  private gtag(...args: unknown[]): void {
    window.dataLayer = window.dataLayer || [];
    // Den kanonischen gtag-Shim aus index.html wiederverwenden: er pusht ein
    // arguments-Objekt, das gtag.js zuverlässig als Command verarbeitet. Ein
    // echtes Array würde nicht zuverlässig erkannt.
    if (typeof window.gtag === 'function') {
      window.gtag(...args);
    } else {
      // Fallback: arguments-Objekt nachbilden (NICHT als Array pushen).
      function gtagShim(this: unknown) {
        window.dataLayer.push(arguments);
      }
      (gtagShim as (...a: unknown[]) => void)(...args);
    }
  }
}
